"use client";

import { c as _c } from "react/compiler-runtime";
import type { Offer } from "@local/offer/src/types";
import Image, { type StaticImageData } from "next/image";
import { useSearchParams } from "next/navigation";
import { useMemo, useRef } from "react";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket } from "@local/i18n/src/languages";
import { pdp } from "@local/i18n/src/catalogs/pdp";
import { priceSr } from "@local/i18n/src/catalogs/priceSr";
import { a11y } from "@local/i18n/src/catalogs/a11y";
import { hasRealDiscount } from "../../ui/CompareAtPrice";
import { Money } from "../../ui/Money";
import { PriceViewBeacon } from "@local/tracking/src/beacons/price_view";
interface HighlightPriceProps {
  market: string;
  product: Offer.PinProduct;
  prices?: Offer.ListingPrices;
  promoImage: string | StaticImageData;
  promoImageMobile?: string | StaticImageData;
  /**
   * Alt text for the promo image. The image carries the offer message
   * ("LIMITED TIME ONLY! 60% OFF"), so it is informative, not decorative —
   * prefer the CMS `media.alt`; falls back to the translated `a11y.promoImageAlt`.
   */
  promoImageAlt?: string;
  channel?: string;
  /** CTA block id of the enclosing highlight (e.g. "cta-block-11"), for segmentation. */
  callToActionId?: string;
  /**
   * [VV11-TEARDOWN] lp-no-discount-framing A/B test (DEV-10019): test arm —
   * hide the "60% OFF" promo image, the struck-through compare price and the
   * "Save nn%" line. The price actually shown/charged is unchanged; only the
   * discount framing goes.
   */
  hideDiscountFraming?: boolean;
}
export const HighlightPrice = t0 => {
  const $ = _c(27);
  const {
    market,
    product,
    prices,
    promoImage,
    promoImageMobile,
    promoImageAlt,
    channel,
    callToActionId,
    hideDiscountFraming: t1
  } = t0;
  const hideDiscountFraming = t1 === undefined ? false : t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[0] !== searchParams) {
    t2 = searchParams?.get("item_id");
    $[0] = searchParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const itemId = t2;
  const t = getT(getLanguageForMarket(market), {
    pdp,
    priceSr,
    a11y
  });
  const priceRef = useRef(null);
  const originalPrice = prices?.[0]?.value ?? product.msrp;
  const rawDiscount = prices?.[0]?.discountValue;
  const defaultDiscountPrice = rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice;
  let matchedModifierPrice;
  let hasSpecificSkuMatch = false;
  if ($[2] !== itemId || $[3] !== prices || $[4] !== product.skuLists || $[5] !== product.skus || $[6] !== product.type) {
    const allSkus = product.type === "BUNDLE" ? product.skuLists.flatMap(_temp) : product.skus;
    const matchingSku = itemId ? allSkus.find(sku => sku.legacyId === itemId || sku.attributes.some(attr => attr.name === "payloadId" && attr.value === itemId) || sku.id === itemId || sku.name === itemId) : allSkus.find(_temp2);
    if (matchingSku) {
      hasSpecificSkuMatch = true;
      if (matchingSku.name && prices?.[0]?.modifiers?.length) {
        const matchedModifier = prices[0].modifiers.find(modifier => modifier.skus.includes(matchingSku.name as string));
        if (matchedModifier) {
          matchedModifierPrice = matchedModifier.amount;
        }
      }
    }
    $[2] = itemId;
    $[3] = prices;
    $[4] = product.skuLists;
    $[5] = product.skus;
    $[6] = product.type;
    $[7] = hasSpecificSkuMatch;
    $[8] = matchedModifierPrice;
  } else {
    hasSpecificSkuMatch = $[7];
    matchedModifierPrice = $[8];
  }
  let discountPrice;
  if (hasSpecificSkuMatch) {
    discountPrice = matchedModifierPrice ?? defaultDiscountPrice;
  } else {
    let t3;
    if ($[9] !== prices) {
      t3 = prices?.[0]?.modifiers?.length ? prices[0].modifiers.reduce(_temp3, prices[0].modifiers[0].amount) : undefined;
      $[9] = prices;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    const lowestModifierPrice = t3;
    discountPrice = lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice ? lowestModifierPrice : defaultDiscountPrice;
  }
  let t3;
  if ($[11] !== discountPrice || $[12] !== originalPrice) {
    t3 = Math.round((originalPrice - discountPrice) / originalPrice * 100);
    $[11] = discountPrice;
    $[12] = originalPrice;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const discountPercentage = t3;
  let t4;
  if ($[14] !== discountPercentage || $[15] !== discountPrice || $[16] !== originalPrice) {
    t4 = {
      originalPrice,
      discountPrice,
      discountPercentage
    };
    $[14] = discountPercentage;
    $[15] = discountPrice;
    $[16] = originalPrice;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const priceData = t4;
  let t5;
  if ($[18] !== hideDiscountFraming || $[19] !== priceData.discountPrice || $[20] !== priceData.originalPrice) {
    t5 = !hideDiscountFraming && hasRealDiscount(priceData.originalPrice, priceData.discountPrice);
    $[18] = hideDiscountFraming;
    $[19] = priceData.discountPrice;
    $[20] = priceData.originalPrice;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const discounted = t5;
  const promoAlt = promoImageAlt?.trim() || t("a11y.promoImageAlt");
  let t6;
  if ($[22] !== callToActionId || $[23] !== channel || $[24] !== market || $[25] !== priceData.discountPrice) {
    t6 = <PriceViewBeacon targetRef={priceRef} market={market} priceInCents={priceData.discountPrice} channel={channel} ctaId={callToActionId} />;
    $[22] = callToActionId;
    $[23] = channel;
    $[24] = market;
    $[25] = priceData.discountPrice;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return <div className="relative">{!hideDiscountFraming && <div className="mb-4 flex justify-center">{promoImageMobile ? <><Image alt={promoAlt} height={100} src={promoImage} width={400} className="hidden w-full md:block" /><Image alt={promoAlt} height={100} src={promoImageMobile} width={400} className="w-full md:hidden" /></> : <Image alt={promoAlt} height={100} src={promoImage} width={400} className="w-full" />}</div>}<div className="my-4 text-center">{discounted && <p className="text-base font-normal text-gray-600 line-through"><Money value={priceData.originalPrice} market={market} srLabel={t("priceSr.Original price")} /></p>}<p ref={priceRef} className="font-display text-5xl font-semibold text-green-700 uppercase"><Money value={priceData.discountPrice} market={market} srLabel={discounted ? t("priceSr.Sale price") : t("priceSr.Price")} /></p>{t6}{discounted && <p className="text-xl font-semibold text-green-700 uppercase">{t("pdp.savePercent", {
          pct: priceData.discountPercentage
        })}</p>}</div></div>;
};
function _temp(skuList) {
  return skuList.skus;
}
function _temp2(sku_0) {
  return sku_0.isPreselected === true;
}
function _temp3(min, m) {
  return m.amount < min ? m.amount : min;
}